/**
 * Phase 4 plan 04-06 — 4-state health badge (D-22).
 *
 * Pure-function classification given the backend fields
 * (`last_test_success`, `last_tested_at`). Each variant pairs a colour, an
 * icon and an i18n label (UI-SPEC accessibility §"Color is not the sole
 * signal").
 */
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, Circle, XCircle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export type ApiKeyHealthStatus = 'healthy' | 'stale' | 'failed' | 'untested'

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000

// eslint-disable-next-line react-refresh/only-export-components -- helper + component live in same module by design
export function computeHealthStatus(
  lastTestSuccess: boolean | null,
  lastTestedAt: string | null,
): ApiKeyHealthStatus {
  if (lastTestedAt === null) return 'untested'
  if (lastTestSuccess === false) return 'failed'
  const age = Date.now() - new Date(lastTestedAt).getTime()
  if (age > STALE_THRESHOLD_MS) return 'stale'
  return 'healthy'
}

interface ApiKeyHealthBadgeProps {
  /**
   * Either the raw backend fields… (use `computeHealthStatus` internally)
   */
  lastTestSuccess?: boolean | null
  lastTestedAt?: string | null
  /**
   * …or the pre-computed status (primarily for storybook / tests).
   */
  status?: ApiKeyHealthStatus
  /** Backend error string to surface in the failed-state tooltip. */
  lastError?: string | null
}

interface VariantConfig {
  variant: 'success' | 'secondary' | 'destructive' | 'warning'
  Icon: typeof Check
}

const VARIANT_MAP: Record<ApiKeyHealthStatus, VariantConfig> = {
  healthy: { variant: 'success', Icon: Check },
  stale: { variant: 'secondary', Icon: AlertTriangle },
  failed: { variant: 'destructive', Icon: XCircle },
  untested: { variant: 'warning', Icon: Circle },
}

export function ApiKeyHealthBadge({
  lastTestSuccess,
  lastTestedAt,
  status: explicitStatus,
  lastError,
}: ApiKeyHealthBadgeProps) {
  const { t } = useTranslation()
  const status =
    explicitStatus ??
    computeHealthStatus(lastTestSuccess ?? null, lastTestedAt ?? null)
  const { variant, Icon } = VARIANT_MAP[status]
  const label = t(`settings.llmKeys.status.${status}`)

  const badge = (
    <Badge
      variant={variant}
      aria-label={label}
      className="inline-flex items-center gap-1"
    >
      <Icon className="h-3 w-3" />
      {label}
    </Badge>
  )

  if (status === 'failed' && lastError) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span>{badge}</span>
        </TooltipTrigger>
        <TooltipContent>{lastError}</TooltipContent>
      </Tooltip>
    )
  }

  return badge
}
